export type ProductStatus = 'published' | 'draft' | 'archived' | 'scheduled'
export type StockLevel = 'in' | 'low' | 'out'
export type ProductVisibility = 'public' | 'members_only' | 'hidden'
export type ProductModalKind = 'delete' | 'bulkConfirm' | null
export type BulkProductAction = 'publish' | 'draft' | 'archive' | 'map' | 'delete'

// Bulk category/brand assignment ("Map") payload. Sent to the bulk products
// PUT endpoint alongside the {store_id, organization_id, product_ids} envelope.
// Field names mirror the per-product update payload (categories[], brand id).
export interface BulkAssignPayload {
  categories?: { id: number; name: string; slug: string }[]
  brand?: number | null
}
export type CannabinoidUnit = '%' | 'mg' | 'ml' | 'mg/ml' | 'mg/unit'
export type StoreType = 'cova' | 'greenline' | 'lightspeed' | 'flowhub'

export interface ProductImage {
  id: string
  src: string
  alt?: string
  title?: string
  caption?: string
  isFeatured?: boolean
  // Responsive set as returned by the backend: a width->URL map
  // (e.g. { '640': url, '1024': url }), or {} when no responsive set exists.
  srcset?: Record<string, string>
}

export interface CannabinoidEntry {
  name: 'THC' | 'CBD' | 'CBN' | 'CBG'
  min?: number
  max?: number
  unit: CannabinoidUnit
}

export interface CustomSpecification {
  key: string
  value: string
}

export interface ProductLocationStock {
  locationId: string
  locationName: string
  stockQty: number
  threshold?: number
}

export interface ProductVariant {
  id: string
  name: string
  sku: string
  price: number
  salePrice?: number
  cogs?: number
  stock: StockLevel
  stockQty: number
  netWeight?: string
  cannabisEquiv?: string
  hcClass?: string
  gtin?: string
  isCurrent?: boolean
  availability?: string
}

export interface LinkedProduct {
  id: string
  name: string
  sku?: string
  price?: number
  stock_quantity?: number
  images?: { src: string }[]
}

export interface VariationGroupLocationInventory {
  available: number
  reserved: number
  booked: number
  in_transit: number
}

export interface VariationGroupLocationCovaMapping {
  CatalogItemId?: string
  LocationId?: string
  warehouse_id?: string
}

export interface VariationGroupLocation {
  id: number
  sku?: string
  price?: string
  regular_price?: string
  sale_price?: string
  bs_sale_price?: string | null
  bs_date_on_sale_from_gmt?: string | null
  bs_date_on_sale_to_gmt?: string | null
  date_on_sale_from_gmt?: string | null
  date_on_sale_to_gmt?: string | null
  location_name?: string
  stock_quantity?: number
  inventory_quantity?: number
  threshold?: number
  inventory?: VariationGroupLocationInventory
  inventory_id?: string | null
  cova_mapping?: VariationGroupLocationCovaMapping
  image?: { name?: string; alt?: string; src?: string }
  images?: { name?: string; alt?: string; src?: string }[]
  description?: string
  gtin?: string
  upc?: string
  _custom_product_meta_gtin?: string | null
  _custom_product_meta_upc?: string | null
  plant_type?: string
  weight?: number
  cannabis_weight?: number
  volume?: number | null
  volume_unit?: string
  thc_min?: number | null
  thc_max?: number | null
  thc_unit?: string
  cbd_min?: number | null
  cbd_max?: number | null
  cbd_unit?: string
  cbn_min?: number | null
  cbn_max?: number | null
  cbn_unit?: string
  cbg_min?: number | null
  cbg_max?: number | null
  cbg_unit?: string
  terpenes?: unknown[]
  terpenes_total?: number | null
  terpenes_total_unit?: string
  effects?: string[]
  flavours?: string[]
  bs_custom_specifications?: CustomSpecification[]
  specifications?: Record<string, string | number | null>
  ids?: number[]
}

export interface VariationGroupCatalog {
  CatalogItemId?: string
  Manufacturer?: string
  ManufacturerSkus?: { Manufacturer?: string; SKU?: string }[]
  MasterName?: string
  MasterProductName?: string
  Name?: string
  Prices?: unknown[]
  SupplierSkus?: { Supplier?: string; SKU?: string }[]
  Upcs?: string[]
  VariationId?: string
}

// Wire shape of a variation's image sub-doc. The backend variation ImageSerializer
// currently whitelists name/alt/src only; `caption` round-trips once the backend
// ImageSerializer whitelists it (see be_libs/utils/pantry_product.py).
export interface VariationImage {
  name?: string
  alt?: string
  src?: string
  caption?: string
}

// Per-variation image edit stashed on the draft (keyed by VariationGroupItem._id).
// A `null` entry means the image was removed/cleared.
export type VariationImagePatch = Pick<VariationImage, 'src' | 'alt' | 'name' | 'caption'>

export interface VariationGroupItem {
  _id: string
  name?: string
  description?: string
  status?: string
  type?: string
  short_sku?: string
  gtin?: string
  upc?: string
  sale_price?: string
  date_on_sale_from_gmt?: string | null
  date_on_sale_to_gmt?: string | null
  image?: VariationImage
  images?: VariationImage[]
  attributes?: { id: number; name: string; option: string }[]
  plant_type?: string
  weight?: number
  cannabis_weight?: number
  volume?: number | null
  volume_unit?: string
  thc_min?: number | null
  thc_max?: number | null
  thc_unit?: string
  cbd_min?: number | null
  cbd_max?: number | null
  cbd_unit?: string
  cbn_min?: number | null
  cbn_max?: number | null
  cbn_unit?: string
  cbg_min?: number | null
  cbg_max?: number | null
  cbg_unit?: string
  terpenes?: unknown[]
  terpenes_total?: number | null
  terpenes_total_unit?: string
  effects?: string[]
  flavours?: string[]
  bs_custom_specifications?: CustomSpecification[]
  ids?: number[]
  total_available?: number
  location_has_available?: number
  catalog_item_id?: string
  unaltered_slug?: string
  cost?: number
  deposit_fee?: number
  catalog?: VariationGroupCatalog
  locations?: VariationGroupLocation[]
}

export interface VariationLocationQuantity {
  name?: string
  variations: {
    name: string
    inventory_quantity: number
    stock_quantity: number
  }[]
}

export interface VariationQuantity {
  locations: Record<string, VariationLocationQuantity>
  total_quantity: number
  total_stock_quantity: number
}

export interface ProductListVariation {
  id: string
  name: string
  sku: string
  image?: ProductImage
  categoryName?: string
  subCategories?: string[]
  brand?: string
  price: number
  minPrice?: number
  maxPrice?: number
  salePrice?: number
  saleMinPrice?: number
  saleMaxPrice?: number
  stock: StockLevel
  stockQty: number
  status: ProductStatus
  date_created_gmt?: string
  quantity?: VariationQuantity
}

export interface Product {
  id: string
  name: string
  slug: string
  sku: string
  brand?: string
  categoryId?: string
  categoryIds?: string[]
  categoryName?: string
  subCategories?: string[]
  hcClass?: string
  price: number
  minPrice?: number
  maxPrice?: number
  salePrice?: number
  cogs?: number
  status: ProductStatus
  stock: StockLevel
  stockQty: number
  locationStock?: ProductLocationStock[]
  mapped: boolean
  image?: ProductImage
  images?: ProductImage[]
  shortDescription?: string
  longDescription?: string
  tags?: (string | number)[]
  terpenes?: string[]
  effects?: string[]
  flavours?: string[]
  cannabinoids?: CannabinoidEntry[]
  seoTitle?: string
  seoDescription?: string
  seoKeyphrase?: string
  canonicalUrl?: string
  netWeight?: string
  cannabisEquiv?: string
  posProductName?: string
  posSku?: string
  // Raw POS-link status: 'syncing' (POS-linked, incl. Budsense) | 'disconnect' (manual entry).
  // Defaults to 'syncing' at the mapper when the API omits the field, matching legacy behavior.
  posStatus?: string
  // True while a POS resync (triggered via productApis.syncFromPos) is still running on the backend.
  waitingSyncingFromPos?: boolean
  gtin?: string
  upc?: string
  variants?: ProductVariant[]
  variations?: ProductListVariation[]
  visibility?: ProductVisibility
  posIntegration?: string
  updatedAt?: string
  createdAt?: string
  date_created_gmt?: string
  publishedAt?: string
  scheduledAt?: string
  upsellIds?: string[]
  relatedIds?: string[]
  upsellData?: LinkedProduct[]
  relatedData?: LinkedProduct[]
  aiCredits?: { used: number; remaining: number; total: number }
  locationSpecificData?: boolean
  // Backend-computed Page Score (0-100) from the list payload's `page_score`.
  // Read-only; undefined when the API omits it or sends a non-finite value.
  pageScore?: number
  // Per-area breakdown from the list payload's `page_score_data.areas`, used by
  // the list's score tooltip. Undefined when the API omits or mangles it.
  pageScoreAreas?: ProductPageScoreAreaResult[]
}

// One backend Page Score area (`page_score_data.areas[]`).
export interface ProductPageScoreAreaResult {
  id: PageScoreAreaId
  // Completion fraction in [0, 1].
  value: number
  // Backend status copy: 'Assigned' | 'Present' | 'Complete' | 'Missing' | 'Can be improved'.
  status: string
  // Failing sub-checks (Specifications / SEO only); empty otherwise.
  issues: string[]
}

export interface ProductDraft {
  name: string
  slug: string
  shortDescription: string
  longDescription: string
  images: ProductImage[]
  tags: (string | number)[]
  terpenes: string[]
  effects: string[]
  flavours: string[]
  bs_custom_specifications: CustomSpecification[]
  cannabinoids: CannabinoidEntry[]
  seoTitle: string
  seoDescription: string
  seoKeyphrase: string
  canonicalUrl: string
  volume: number | null
  gtin: string
  upc: string
  categoryIds: string[]
  categories?: { id: string; name: string; slug: string }[]
  brand: string
  status: ProductStatus
  scheduledAt: string
  visibility: ProductVisibility
  upsellIds: string[]
  relatedIds: string[]
  upsellData: LinkedProduct[]
  relatedData: LinkedProduct[]
  variationData?: VariationGroupItem[]
  // Per-variation image edits, keyed by VariationGroupItem._id. A `null` entry
  // clears the image. Applied onto each item in buildVariationData at save time.
  variationImageOverrides?: Record<string, VariationImagePatch | null>
  locationSpecificData?: boolean
}

export type ProductSortBy = 'name' | 'date_created_gmt' | 'page_score'

// Backend `page_score_states` filter values: good 75–100, weak 45–74, bad 0–44.
export type PageScoreState = 'good' | 'weak' | 'bad'
// Canonical order — used to normalise URL/filter state.
export const PAGE_SCORE_STATES: PageScoreState[] = ['good', 'weak', 'bad']

// Page Score areas — the editor engine's area ids and the backend
// `page_score_areas` filter values ("needs improvement": area value < 1).
export type PageScoreAreaId = 'brand' | 'category' | 'image' | 'description' | 'specs' | 'seo'
// Canonical order — used to normalise URL/filter state.
export const PAGE_SCORE_AREAS: PageScoreAreaId[] = ['brand', 'category', 'image', 'description', 'specs', 'seo']

export interface ProductListParams {
  page: number
  limit: number
  search?: string
  status?: ProductStatus | 'all'
  // Multi-select: the backend matches ANY of the ids (Mongo `$in`). Sent as
  // repeated `category_ids` / `brands` query keys.
  categoryIds?: string[]
  brandIds?: string[]
  stockQuantityMin?: number
  stockQuantityMax?: number
  locationId?: string
  hcClass?: string
  mapping?: 'mapped' | 'unmapped' | 'all'
  posStatus?: 'disconnect' | 'syncing'
  // Sent as repeated `page_score_states` keys. Omit/empty = all (incl. unscored).
  pageScoreStates?: PageScoreState[]
  // Sent as repeated `page_score_areas` keys (OR within, AND with states).
  pageScoreAreas?: PageScoreAreaId[]
  sortBy?: ProductSortBy
  sort?: 1 | -1
  // User UTC-offset (hours). Only sent on CSV export so the backend formats
  // datetime columns in the user's local time; ignored by the list query.
  timeZone?: number
}

// Export-CSV response. The backend returns one of two shapes:
//   • `{ csv }`       — small filtered sets stream the CSV body inline; the
//                       frontend downloads it immediately.
//   • `{ export_id }` — large sets are queued as an async backend job; the file
//                       is delivered later via the push-notification system.
export interface ProductExportResponse {
  csv?: string
  export_id?: string
}

export interface ProductListResponse {
  data: Product[]
  totalItems: number
  totalPages: number
  all: number
  published: number
  draft: number
  archived: number
  // Backend `page_score_needs_improvement`: count of products matching all
  // current filters (page-score ones included) that the "Needs Improvement"
  // card represents — score < 75 (Weak + Bad). NOTE: until the backend reverts
  // to the score-based count it counts products with any incomplete area.
  // Absent on non-pantry stores.
  pageScoreNeedsImprovement?: number
}
